import React from "react";
import { useDispatch, useSelector } from "react-redux";
import { useSearchParams } from "react-router-dom";
import { useEffect } from "react";
import { getBalanceAndPin } from "../../features/balance-and-pin/balanceAndPinSlice";
import Spinner from "../../components/Spinner";
import {
  IntroSection,
  MainContainer,
  SectionContainer,
  AppStoreLinks,
  PaymentAccount,
  StepNumber,
  Footer
} from "./styles";

import intro from "./images/logo.svg";
import wearable from "./images/wearable.png";
import boboLogo from "./images/bobo-logo.svg";
import copyIcon from "./images/copy-icon.svg";
import orLine from "./images/or-line.svg";
import appStoreLogo from "./images/app-store.svg";
import gplayLogo from "./images/play-store.svg";
import curveFlags from "./images/curve-flags.svg";
import payAnyWhereImage from "../tap2start/img/pay-anywhere-image.png";
import digiseqLogo from "./images/digiseq-logo.png";
import { formattedBalance } from "../../features/balance-and-pin/utils";

const LiveNation25 = () => {
  const [searchParams] = useSearchParams();
  const serialNumber = searchParams.get("serialNumber");
  const provider = searchParams.get("provider");

  const dispatch = useDispatch();
  const { balanceAndPin, isLoading } = useSelector(
    (state) => state.balanceAndPin
  );

  useEffect(() => {
    if (!balanceAndPin && serialNumber) {
      dispatch(
        getBalanceAndPin({
          serial: serialNumber,
          query: `?provider=${provider || "pannovate"}`
        })
      );
    }
  }, [balanceAndPin, serialNumber, dispatch, provider]);

  const appStoreLinks = (appStoreUrl, playStoreUrl) => {
    return (
      <AppStoreLinks>
        <img
          src={appStoreLogo}
          alt="app-store"
          onClick={() => window.open(appStoreUrl, "_blank")}
        />
        <img
          src={gplayLogo}
          alt="play-store"
          onClick={() => window.open(playStoreUrl, "_blank")}
        />
      </AppStoreLinks>
    );
  };

  if (isLoading) return <Spinner />;

  return (
    <MainContainer>
      <IntroSection>
        <div className="intro-logo">
          <img src={intro} alt="intro" />
        </div>
        <h1>Welcome to your Priceless Experience</h1>
        <div className="intro-content">
          <p>
            We’re thrilled to have you here — get ready for an unforgettable
            evening!
          </p>
          <p>
            Your <strong>Mastercard keyring wearable</strong> is preloaded with
            €20 to make your night even more special.
          </p>
          <p>
            Whether it’s a drink, a snack, or a little something extra, just{" "}
            <strong>tap and enjoy!</strong>
          </p>
        </div>
      </IntroSection>
      <SectionContainer>
        <div className="balance-container">
          <h2>Your Balance</h2>
          <p className="balance-value">
            {formattedBalance(
              balanceAndPin?.balanceDetails?.availableBalance,
              balanceAndPin?.balanceDetails?.currency
            )}
          </p>
          <p>
            <i>
              Your balance will update automatically after each transaction.
            </i>
          </p>
        </div>
      </SectionContainer>
      <SectionContainer>
        <div className="how-it-works">
          <div className="wearable-container">
            <img src={wearable} alt="wearable" />
          </div>
          <h2>How It Works</h2>
          <p>
            Your keyring is preloaded with <strong>€20</strong>. Simply{" "}
            <strong> tap it on any contactless payment terminal </strong> where
            Mastercard Contactless is enabled to make a purchase.
          </p>
          <p>
            If you make a purchase in a currency other than €, the amount will
            be converted using the{" "}
            <strong>
              foreign exchange rate in effectp on the date of the transaction.
            </strong>{" "}
            Any unused balance will remain available for use at any merchants
            after the event.
          </p>
        </div>
      </SectionContainer>
      <SectionContainer>
        <PaymentAccount>
          <h2>Payment Account</h2>
          <p>
            Once you've enjoyed your €20, it's time to make the keyring truly
            yours! Choose one of the following ways to upgrade it:
          </p>
          <h3>Prepaid partner</h3>
          <div className="prepaid-partner-container">
            <div className="bobo-logo-container">
              <img src={boboLogo} alt="prepaid partner" />
            </div>
            <p>
              Activate it with Bourgeois Bohème wallet and enjoy unrivaled
              payment experience!
            </p>
            <p>Your keyfob activation code</p>
            <div className="activation-code">
              <h2>{balanceAndPin?.activationCode || "N/A"}</h2>
              <img
                src={copyIcon}
                alt="copy"
                onClick={() =>
                  navigator.clipboard.writeText(balanceAndPin?.activationCode)
                }
              />
            </div>
            <div
              className="button"
              onClick={() =>
                window.open(
                  `https://events.bobofin.com/?token=${balanceAndPin?.activationCode}`,
                  "_blank"
                )
              }
            >
              <span>Join Bourgeois Bohème</span>
            </div>
          </div>
          <div className="or-separator">
            <img src={orLine} alt="or" />
          </div>
          <h3>Link your card</h3>
          <div className="curve-container">
            <img src={curveFlags} alt="curve flags" />
          </div>
          <StepNumber>1</StepNumber>
          <h2 className="step-title">Create a Curve Account</h2>
          <p>
            Register your card details with Curve to receive a free compatible
            digital bank card.
          </p>
          <p>Download the Curve App:</p>
          {appStoreLinks(
            "https://apps.apple.com/gb/app/curve-supercharge-your-money/id1049397112",
            "https://play.google.com/store/apps/details?id=com.imaginecurve.curve.prd"
          )}
          <StepNumber>2</StepNumber>
          <h2 className="step-title">
            Link Your Curve Virtual Card Using the Manage-Mii App
          </h2>
          <p>
            Follow the instructions on the Manage-Mii app and use your new Curve
            Card details when prompted.
          </p>
          {appStoreLinks(
            "https://apps.apple.com/app/manage-mii/id6446908503",
            "https://play.google.com/store/apps/details?id=com.digiseq.managemii"
          )}
          <StepNumber>3</StepNumber>
          <h2 className="step-title">Pay Anywhere!</h2>
          <p>
            Tap your wearable at any card terminal where Mastercard Contactless
            is enabled.
          </p>
          <img src={payAnyWhereImage} alt="pay anywhere" />
        </PaymentAccount>
      </SectionContainer>
      <Footer>
        <p>Powered by</p>
        <img
          src={digiseqLogo}
          alt="digiseq"
          onClick={() =>
            window.open(
              "https://www.digiseq.co.uk",
              "_blank noreferrer noopener"
            )
          }
        />
        <p>Payment services provided by</p>
        <img
          src={boboLogo}
          alt="payme-swiss"
          onClick={() =>
            window.open("https://www.bobofin.com", "_blank noreferrer noopener")
          }
        />
      </Footer>
    </MainContainer>
  );
};

export default LiveNation25;
